import React from 'react';
import { TextField } from '../../fields/TextField';
import { SelectTypeaheadMulti } from '../../fields/SelectTypeaheadMulti';
import { SelectTypeahead } from '../../fields/SelectTypeahead';
import { TypeaheadOption } from '../../atoms/SelectTypeahead/parts/types';
import { RadioGroupAtom } from '../../atoms/RadioGroup';
import { CustomFieldPayloadModel, ElementParams, SmartformElementData } from '../../types';
import { getElementFields } from '../../forms/utils/getElementFields';
import { DatePicker } from '../../fields/DatePicker';
import { DateTimePicker } from '../../fields/DateTimePicker';
import { PermissionHelper } from '../../atoms/common/PermissionHelper';
import { useFieldSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { TimePicker } from '../../fields/TimePicker';
import { formatDate } from '../../forms/utils/dateHelpers';
import { logger } from '../../logger';
import { Typography } from '@utmost.co/utmost-design-system';
import styled from 'styled-components';

export interface CustomFieldInputModel {
  definition: CustomFieldPayloadModel | undefined;
  value?: string | null;
}
interface CustomFieldMultiInputModel {
  definition: CustomFieldPayloadModel | undefined;
  values: string[] | null;
}

export type CustomFieldProps = {
  loading?: boolean;
  disabled?: boolean;
  onFilterChange?: (value: string) => void;
  onChange?: (value: unknown) => void;
  onMissingOption?: (value: string) => TypeaheadOption<string>;
};

const RenderField = (
  customField: CustomFieldPayloadModel | undefined,
  loading: boolean | undefined,
  name: string,
  helperText: string | undefined,
  infoText: string | undefined,
  label: string,
  touched: boolean,
  errors: string | undefined,
  placeholder: string | undefined,
  required: boolean,
  excludedValues?: string[],
  params?: ElementParams,
  initialValue?: string,
  sort?: boolean,
  onFilterChange?: (value: string) => void,
  onMissingOption?: (value: string) => TypeaheadOption<string>,
  onChange?: (value: unknown) => void,
  disabled?: boolean
) => {
  const { getFieldPermissions, permissionDebugger, translations } = useFieldSmartFormContext(name);
  const customFieldPermission = getFieldPermissions(name);
  let isDisabled = !!disabled;

  if (!loading && !customField) isDisabled = true;

  const customFieldType = customField?.dataType;
  const getTextfieldType = (): React.InputHTMLAttributes<unknown>['type'] => {
    if (customFieldType === 'HYPERLINK') return 'url';
    if (customFieldType === 'INTEGER' || customFieldType === 'DECIMAL') return 'number';

    return 'text';
  };

  if (!customField)
    return errors ? (
      <>
        <StyledError dataTest="custom-field-error">
          {translations?.hiddenFieldHasErrors({ name, b: (value: string[]) => <b>{value}</b> })}
          <div>{errors}</div>
        </StyledError>
        {permissionDebugger && <PermissionHelper permission={customFieldPermission} />}
      </>
    ) : null;

  switch (customFieldType) {
    case 'ENUM':
    case 'COST_CENTER':
      if (customField?.multivalued) {
        return (
          <SelectTypeaheadMulti<string, CustomFieldMultiInputModel | null>
            name={name}
            label={label}
            loading={loading}
            disabled={isDisabled}
            placeholder={placeholder}
            required={required}
            excludedValues={excludedValues}
            sort={sort}
            fullWidth
            onMissingOption={onMissingOption}
            onFilterChange={onFilterChange}
            helpText={helperText}
            infoText={infoText}
            nullifyNoneOption
            initialValue={initialValue}
            onSetFieldValue={onChange}
            values={customField?.values.map((field) => ({
              id: field.value,
              label: field.label,
              value: field.value,
              code: field.code
            }))}
            mapInputValue={(value) => {
              if (!value) {
                return [];
              }
              return value.values || [];
            }}
            mapOutputValue={(values) => {
              const numElements = values?.length || 0;
              return {
                definition: customField,
                values: numElements === 0 ? null : values!
              };
            }}
            mapReadOnlyValue={(field) => {
              if (!field?.values || Array.isArray(field?.values)) {
                return (field?.values || []).join(', ');
              } else {
                try {
                  logger.warn(`Invalid mapReadOnlyValue mapping.`, JSON.stringify(field?.values ?? []));
                } catch {}
                return '';
              }
            }}
          />
        );
      }
      if (params?.style === 'buttons') {
        return (
          <div>
            <RadioGroupAtom<string, CustomFieldInputModel>
              name={name}
              label={label}
              required={required}
              helpText={helperText}
              infoText={infoText}
              errors={errors}
              touched={touched}
              disabled={isDisabled || loading}
              values={customField?.values || []}
              onSetFieldValue={onChange}
              mapInputValue={(customFieldInput) => customFieldInput?.value || undefined}
              mapOutputValue={(value) => ({
                value: value ?? '',
                definition: customField
              })}
            />
            {permissionDebugger && <PermissionHelper permission={customFieldPermission} />}
          </div>
        );
      }

      return (
        <SelectTypeahead<string, CustomFieldInputModel | null>
          name={name}
          label={label}
          loading={loading}
          disabled={isDisabled}
          placeholder={placeholder}
          required={required}
          excludedValues={excludedValues}
          helpText={errors || helperText}
          infoText={infoText}
          initialValue={initialValue}
          fullWidth
          nullifyNoneOption
          onMissingOption={onMissingOption}
          onFilterChange={onFilterChange}
          onSetFieldValue={onChange}
          values={(customField?.values || []).map((field) => ({
            id: field.value,
            label: field.label,
            value: field.value,
            code: field.code
          }))}
          sort={sort}
          mapInputValue={(value) => value?.value || ''}
          mapOutputValue={(value) => ({
            value: value ?? null,
            definition: customField
          })}
          mapReadOnlyValue={(field) => field?.value || undefined}
        />
      );

    case 'OBJECT':
      return (
        <SelectTypeahead<string, CustomFieldInputModel | null>
          name={name}
          label={label}
          placeholder={placeholder}
          required={required}
          excludedValues={excludedValues}
          helpText={helperText}
          infoText={infoText}
          initialValue={initialValue}
          loading={loading}
          disabled={isDisabled}
          fullWidth
          onMissingOption={onMissingOption}
          onValueChange={onChange}
          onFilterChange={onFilterChange}
          values={
            (customField?.values || []).map((field) => ({
              id: field.value,
              label: field.label,
              value: field.value,
              code: field.code
            })) ?? []
          }
          sort={sort}
          nullifyNoneOption
          mapInputValue={(value) => value?.value || ''}
          mapOutputValue={(value) => ({ value: value ?? null, definition: customField })}
          mapReadOnlyValue={(field) => field?.value ?? undefined}
        />
      );
    case 'DATE':
      return (
        <DatePicker
          name={name}
          label={label}
          disabled={isDisabled || loading}
          required={required}
          helpText={helperText}
          dateFormat={params?.dateFormat}
          mapInputValue={(value: any) => value?.value} // Need to map input value as is nested in 'value' object.
          onChange={(value, setValue) => {
            if (onChange) {
              onChange({
                value,
                definition: customField
              });
            } else {
              setValue({
                value,
                definition: customField
              });
            }
          }}
        />
      );
    case 'DATETIME':
      return (
        <DateTimePicker
          name={name}
          label={label}
          placeholder={placeholder}
          disabled={isDisabled || loading}
          required={required}
          helpText={helperText}
          mapInputValue={(value: any) => value?.value ?? ''}
          onChange={(value: any, setValue) => {
            if (onChange) {
              onChange({
                value,
                definition: customField
              });
            } else {
              setValue({
                value,
                definition: customField
              });
            }
          }}
        />
      );
    case 'TIME':
      return (
        <TimePicker
          name={name}
          label={label}
          placeholder={placeholder}
          disabled={isDisabled || loading}
          required={required}
          helpText={helperText}
          mapInputValue={(value: any) => (customField?.multivalued ? (value?.values?.[0] ?? '') : (value?.value ?? ''))}
          onChange={(value: any, setValue) => {
            const data = customField?.multivalued
              ? {
                  values: value ? [formatDate(value, 'HH:mm')] : null,
                  definition: customField
                }
              : {
                  value: value ? formatDate(value, 'HH:mm') : null,
                  definition: customField
                };
            if (onChange) {
              onChange(data);
            } else {
              setValue(data);
            }
          }}
        />
      );
    case 'DOCUMENT':
      return <div>{translations?.docNotSupported || 'Document type not supported'}</div>;
    case 'HYPERLINK':
    case 'INTEGER':
    case 'DECIMAL':
    case 'STRING':
      return (
        <TextField<CustomFieldInputModel>
          name={name}
          label={label}
          placeholder={placeholder}
          fullWidth
          required={required}
          disabled={isDisabled || loading}
          multiline={params?.isMultilineTextField}
          initialValue={initialValue}
          helperText={errors || helperText}
          infoText={infoText}
          type={getTextfieldType()}
          onChange={onChange}
          mapInputValue={(value) => value?.value || ''}
          mapOutputValue={(val?: string) => ({
            value: val ? val : null,
            definition: customField!
          })}
        />
      );

    default:
      return <div>{customField?.dataType} field type not supported</div>;
  }
};

export const CustomField: React.FC<SmartformElementData & CustomFieldProps> = ({
  element,
  loading,
  disabled,
  ...props
}) => {
  const [customField] = getElementFields({
    element: {
      ...element,
      fields:
        element.fields && element.fields.length
          ? element.fields
          : [{ label: element.payload?.customFieldDefinitionModel?.name }]
    },
    ...props
  }).required;

  return RenderField(
    element?.payload?.customFieldDefinitionModel ?? undefined,
    loading,
    customField?.name ?? '',
    customField?.helpText ?? '',
    customField?.infoText ?? '',
    customField?.label ?? '',
    customField?.touched ?? false,
    customField?.error ?? '',
    customField?.hint ?? '',
    customField?.validation?.required ?? false,
    element.params?.excludedValues,
    element.params,
    customField.initialValue,
    element.params?.sort || false,
    props.onFilterChange,
    props.onMissingOption,
    props.onChange,
    disabled
  );
};

const StyledError = styled(Typography)`
  color: ${({ theme }) => theme.colorAlert};
`;
